import type { Board } from "./board";
import type { DamageType } from "./damageType";
import type { Direction } from "./direction";
import type { MusicId } from "./musicId";
import type { ParticleType } from "./particleType";
import type { PhysicalThing } from "./physicalThing";
import type { PhysicalThingWithHp } from "./physicalThingWithHp";
import type { XY } from "./xy";

export const PRIORITY_FIRST = 0;
export const PRIORITY_DEFAULT = 50;
export const PRIORITY_LAST = 99;

export interface ProcessableThing {
	processTurn(board: Board): void;
	getProcessPriority(): number;
}

export function isProcessableThing(thing: unknown): thing is ProcessableThing {
	return (
		typeof thing === "object" &&
		thing !== null &&
		typeof (thing as ProcessableThing).processTurn === "function" &&
		typeof (thing as ProcessableThing).getProcessPriority === "function"
	);
}

export interface MultiPassProcessableThing extends ProcessableThing {
	getPassesToRun(): number[];
}

export function isMultiPassProcessableThing(thing: unknown): thing is MultiPassProcessableThing {
	return isProcessableThing(thing) && typeof (thing as MultiPassProcessableThing).getPassesToRun === "function";
}

export interface OnAddToBoard {
	onAddToBoard(): void;
}

export function isOnAddToBoard(thing: unknown): thing is OnAddToBoard {
	return typeof thing === "object" && thing !== null && typeof (thing as OnAddToBoard).onAddToBoard === "function";
}

export interface ProjectileHittable {
	onProjectileHit(projectile: unknown): void;
	isHittable(): boolean;
}

export function isProjectileHittable(thing: unknown): thing is ProjectileHittable {
	return (
		typeof thing === "object" &&
		thing !== null &&
		typeof (thing as ProjectileHittable).onProjectileHit === "function" &&
		typeof (thing as ProjectileHittable).isHittable === "function"
	);
}

export interface LookAtHorizontalDirection {
	getLookingAtHorizontalDirection(): Direction;
}

export function isLookAtHorizontalDirection(thing: unknown): thing is LookAtHorizontalDirection {
	return (
		typeof thing === "object" &&
		thing !== null &&
		typeof (thing as LookAtHorizontalDirection).getLookingAtHorizontalDirection === "function"
	);
}

export interface PointsToDirection {
	getDirection(): Direction | null;
	setDirection(dir: Direction): void;
}

export function isPointsToDirection(thing: unknown): thing is PointsToDirection {
	return (
		typeof thing === "object" &&
		thing !== null &&
		typeof (thing as PointsToDirection).getDirection === "function" &&
		typeof (thing as PointsToDirection).setDirection === "function"
	);
}

export interface ControllableByAI {
	getBoard(): Board;
	setLookDirection(dir: Direction): void;
	moveTo(xy: XY): void;
	attack(target: PhysicalThingWithHp): void;
	getXY(): XY;
	canMoveTo(xy: XY): boolean;
	isMovementReady(): boolean;
	canAttack(target: PhysicalThingWithHp): boolean;
}

export function isControllableByAI(thing: unknown): thing is ControllableByAI {
	return (
		typeof thing === "object" &&
		thing !== null &&
		typeof (thing as ControllableByAI).getBoard === "function" &&
		typeof (thing as ControllableByAI).setLookDirection === "function" &&
		typeof (thing as ControllableByAI).moveTo === "function" &&
		typeof (thing as ControllableByAI).attack === "function" &&
		typeof (thing as ControllableByAI).getXY === "function" &&
		typeof (thing as ControllableByAI).canMoveTo === "function" &&
		typeof (thing as ControllableByAI).isMovementReady === "function" &&
		typeof (thing as ControllableByAI).canAttack === "function"
	);
}

export enum RobotActorState {
	OFF = "OFF",
	WANDERING = "WANDERING",
	ENGAGING = "ENGAGING",
	ATTACKING = "ATTACKING",
}

export interface RobotActor extends ControllableByAI {
	getState(): RobotActorState;
	setState(state: RobotActorState): void;
}

export function isRobotActor(thing: unknown): thing is RobotActor {
	return (
		isControllableByAI(thing) &&
		typeof (thing as RobotActor).getState === "function" &&
		typeof (thing as RobotActor).setState === "function"
	);
}

export interface Bumpable {
	bump(direction: Direction, author: PhysicalThing): boolean;
}

export function isBumpable(thing: unknown): thing is Bumpable {
	return typeof thing === "object" && thing !== null && typeof (thing as Bumpable).bump === "function";
}

export interface Interactable {
	canInteract(): boolean;
	interact(player: PhysicalThing): void;
}

export function isInteractable(thing: unknown): thing is Interactable {
	return (
		typeof thing === "object" &&
		thing !== null &&
		typeof (thing as Interactable).canInteract === "function" &&
		typeof (thing as Interactable).interact === "function"
	);
}

export interface OnPlayerDriveOver {
	onPlayerDriveOver(): void;
}

export function isOnPlayerDriveOver(thing: unknown): thing is OnPlayerDriveOver {
	return (
		typeof thing === "object" && thing !== null && typeof (thing as OnPlayerDriveOver).onPlayerDriveOver === "function"
	);
}

// Fires before the player is added to the board — board.player is null at dispatch time.
export interface OnPlayerArrive {
	onPlayerArrive(board: Board): void;
	getArrivePriority(): number;
}

export function isOnPlayerArrive(thing: unknown): thing is OnPlayerArrive {
	return (
		typeof thing === "object" &&
		thing !== null &&
		typeof (thing as OnPlayerArrive).onPlayerArrive === "function" &&
		typeof (thing as OnPlayerArrive).getArrivePriority === "function"
	);
}

export interface RemoteDoor {
	doorId: string | null;
	open(): void;
	close(): void;
}

export function isRemoteDoor(thing: unknown): thing is RemoteDoor {
	return (
		typeof thing === "object" &&
		thing !== null &&
		"doorId" in thing &&
		typeof (thing as RemoteDoor).open === "function" &&
		typeof (thing as RemoteDoor).close === "function"
	);
}

export interface SomethingWithState {
	getState(): unknown;
}

export function isSomethingWithState(thing: unknown): thing is SomethingWithState {
	return typeof thing === "object" && thing !== null && typeof (thing as SomethingWithState).getState === "function";
}

export interface PostDeserializable {
	postDeserialize(board: Board): void;
}

export function isPostDeserializable(thing: unknown): thing is PostDeserializable {
	return (
		typeof thing === "object" && thing !== null && typeof (thing as PostDeserializable).postDeserialize === "function"
	);
}

export interface SomethingWithHp {
	getHp(): number;
	getMaxHp(): number;
	applyDamage(amount: number, type: DamageType): void;
}

export function isSomethingWithHp(thing: unknown): thing is SomethingWithHp {
	return (
		typeof thing === "object" &&
		thing !== null &&
		typeof (thing as SomethingWithHp).getHp === "function" &&
		typeof (thing as SomethingWithHp).getMaxHp === "function" &&
		typeof (thing as SomethingWithHp).applyDamage === "function"
	);
}

export interface ParticleEmitter {
	getParticleType(): ParticleType | null;
	getParticleIntensity?(): number; // 0–1, default 1.0
}

export function isParticleEmitter(thing: unknown): thing is ParticleEmitter {
	return (
		typeof thing === "object" &&
		thing !== null &&
		typeof (thing as ParticleEmitter).getParticleType === "function"
	);
}

export interface TintConfig {
	r: number;
	g: number;
	b: number;
	minAlpha: number;
	maxAlpha: number;
}

export interface Tintable {
	getTint(): TintConfig | null;
	getAnimationSpeedMultiplier(): number; // 1.0 = normal, 0.1 = 10x slower, 0 = stopped
}

export function isTintable(thing: unknown): thing is Tintable {
	return (
		typeof thing === "object" &&
		thing !== null &&
		typeof (thing as Tintable).getTint === "function" &&
		typeof (thing as Tintable).getAnimationSpeedMultiplier === "function"
	);
}

export interface LightSource {
	getLightInnerRadius(): number;
	getLightOuterRadius(): number;
}

export function isLightSource(thing: unknown): thing is LightSource {
	return (
		typeof thing === "object" &&
		thing !== null &&
		typeof (thing as LightSource).getLightInnerRadius === "function" &&
		typeof (thing as LightSource).getLightOuterRadius === "function"
	);
}

export interface Toggleable {
	getStateLabel(): string;
	getUseButtonLabel(): string;
}

export function isToggleable(thing: unknown): thing is Toggleable {
	return (
		typeof thing === "object" &&
		thing !== null &&
		typeof (thing as Toggleable).getStateLabel === "function" &&
		typeof (thing as Toggleable).getUseButtonLabel === "function"
	);
}

export interface MusicOverrider {
	getMusicOverride(): MusicId | null;
}

export function isMusicOverrider(thing: unknown): thing is MusicOverrider {
	return (
		typeof thing === "object" &&
		thing !== null &&
		typeof (thing as MusicOverrider).getMusicOverride === "function"
	);
}

// A weapon (or other thing) gated by a per-turn recharge instead of/alongside ammo.
export interface Rechargeable {
	getCooldownTurns(): number; // turns left until usable again; 0 = ready
	tickCooldown(): void; // advance the recharge by one turn
}

export function isRechargeable(thing: unknown): thing is Rechargeable {
	return (
		typeof thing === "object" &&
		thing !== null &&
		typeof (thing as Rechargeable).getCooldownTurns === "function" &&
		typeof (thing as Rechargeable).tickCooldown === "function"
	);
}
